import { useQuery } from '@tanstack/react-query';
import type { CSSProperties } from 'react';
import type { ListModelsResponse } from '@bhops/shared';
import { modelsApi } from '@/api/models';

// ModelPicker — flat <Select> dropdown over the global Model
// registry. Models are small (10s of rows), global, and read-only,
// so a search-on-type combo box like CustomerPicker is overkill.
//
// Used by ContractCreateWizard's line rows (Phase 5-G2 H4c) and
// available for any future surface that needs to pick a Model.id
// from its `code` / `displayName`.
//
// The wire shape comes from `GET /models` (read-only registry,
// added by H4 alongside the wizard).

interface Props {
  /** Currently selected model id, or empty string for "no selection". */
  value: string;
  onChange: (modelId: string) => void;
  /** Disabled state — gated picker behind another field. */
  disabled?: boolean;
  /** Optional inline style overrides (matches `Form.Select`). */
  style?: CSSProperties;
  /** id attribute for `<label htmlFor>` pairing. */
  id?: string;
  /** When true, omit the placeholder "(— 선택 —)" row. */
  required?: boolean;
  /**
   * What the `<option value>` carries and what `onChange` emits:
   * the `Model.id` (default, for FK refs like ContractCreateWizard) or the
   * `Model.code` (for surfaces keyed by code — robot/customer create forms).
   */
  emit?: 'id' | 'code';
}

export function ModelPicker({
  value,
  onChange,
  disabled,
  style,
  id,
  required,
  emit = 'id',
}: Props) {
  const { data } = useQuery<ListModelsResponse>({
    queryKey: ['models'],
    queryFn: () => modelsApi.list(),
    staleTime: 5 * 60_000,
  });

  const items = data?.items ?? [];

  return (
    <select
      id={id}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...baseInput, ...style }}
    >
      {!required && <option value="">— 선택 —</option>}
      {items.map((m) => (
        <option key={m.id} value={emit === 'code' ? m.code : m.id}>
          {m.displayName} ({m.code})
        </option>
      ))}
    </select>
  );
}

const baseInput: CSSProperties = {
  width: '100%',
  height: 34,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
};
